import React, { useState } from "react";
import { AggregationInterval, } from "ee-nestjs-interfaces";
import { useData, Card, HelpTooltip, LineGraph, SingleStat, DateRangeSelector, TimeHorizon, } from "ee-react-components";
import { useAuth0 } from "@auth0/auth0-react";
import { process } from "../../Process";
import { alignmentForTimezone, expandTimeWindowToInterval } from "ee-utils";
import { PowerNodeSocGraph } from "../Graph/PowerNodeSocGraph";
var MultiStatCard = function (_a) {
    var title = _a.title, stats = _a.stats, overlay = _a.overlay;
    return (React.createElement(Card, { className: "grid z-[-10]", spotlights: ["top-[-360px] right-[-230px]", "top-[-150px] left-[-250px]"], noPadding: true },
        React.createElement("div", { className: "overlap-grid flex flex-col gap-y-[16px] p-[16px] z-[-50]" },
            React.createElement("label", { className: "text-black-600 font-N8p5" }, title),
            React.createElement("div", { className: "flex flex-row gap-x-[48px]" }, stats.map(function (s, i) { return (React.createElement(SingleStat, { key: i, title: s[0], value: s[1], reverse: true })); }))),
        overlay != null && (React.createElement("div", { className: "overlap-grid bg-black-800/80 h-full w-full flex flex-col items-center justify-center" },
            React.createElement("label", { className: "font-N7 text-white px-[16px]" }, overlay)))));
};
export var StationPower = function (_a) {
    var _b;
    var details = _a.details, startDate = _a.startDate;
    var _c = useAuth0(), isAuthenticated = _c.isAuthenticated, isLoading = _c.isLoading;
    var _d = useState(TimeHorizon.DAY), timeHorizon = _d[0], setTimeHorizon = _d[1];
    var _e = useState([
        new Date(),
        new Date(),
    ]), dateRange = _e[0], setDateRange = _e[1];
    var selectedYear = dateRange[0].getFullYear();
    var selectedMonthNum = dateRange[0].getMonth() + 1;
    var alignment = alignmentForTimezone(details.timezone || "UTC");
    var powerInterval = timeHorizon === TimeHorizon.DAY
        ? AggregationInterval.Day
        : AggregationInterval.Month;
    // Expand from the start date because it will expand to the full interval (day/month) without also including the next interval.
    var _f = expandTimeWindowToInterval({ start: dateRange[0], end: dateRange[0] }, powerInterval, alignment), powerStart = _f.start, powerEnd = _f.end;
    var powerFrom = new Date(powerStart).toISOString();
    var powerTo = new Date(powerEnd).toISOString();
    var powerTraces = useData("".concat(process.env.CORE_SVC_URL, "/site/power/").concat(details.id, "?from=").concat(encodeURIComponent(powerFrom), "&to=").concat(encodeURIComponent(powerTo)), !isLoading && isAuthenticated, undefined).data;
    // Since there can be multiple powernodes, presumably only one has data points.
    var powerTrace = Object.values(powerTraces !== null && powerTraces !== void 0 ? powerTraces : {}).find(function (v) { return v.length > 0; });
    var powerNodeSocStats = useData("".concat(process.env.CORE_SVC_URL, "/site/soc_history/").concat(details.id, "?from=").concat(encodeURIComponent(powerFrom), "&to=").concat(encodeURIComponent(powerTo)), !isLoading && isAuthenticated, undefined).data;
    // Since there can be multiple powernodes, presumably only one has data points.
    var powerNodeSocHistory = Object.values(powerNodeSocStats !== null && powerNodeSocStats !== void 0 ? powerNodeSocStats : {}).find(function (v) { return v.length > 0; });
    // Fetch selected month power statistics.
    var monthData = useData("".concat(process.env.CORE_SVC_URL, "/site/monthly_power_stats/").concat(details.id, "/").concat(selectedYear, "/").concat(selectedMonthNum), !isLoading && isAuthenticated, undefined).data;
    // Extract peak values.
    var monthPeak = monthData === null || monthData === void 0 ? void 0 : monthData.peakGridPower.valueKw;
    var monthPeakLoadPower = (_b = monthData === null || monthData === void 0 ? void 0 : monthData.peakLoadPower) === null || _b === void 0 ? void 0 : _b.valueKw;
    var labelAccessor = function (d) {
        var date = new Date(d.timestamp);
        if (timeHorizon === TimeHorizon.MONTH) {
            return date.toLocaleDateString([], {
                month: "numeric",
                day: "numeric",
            });
        }
        return date.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };
    var tooltipAccessor = function (d) {
        var date = new Date(d.timestamp);
        if (timeHorizon === TimeHorizon.MONTH) {
            return date.toLocaleString([], {
                month: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
        }
        return "".concat(date.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }));
    };
    var powerValueAccessors = [
        {
            label: "Grid Limit",
            accessor: function (d) { return d.gridLimitKw; },
            omitFill: true,
            dashed: true,
            hideTopTooltip: true,
        },
        {
            label: "Power From Grid",
            accessor: function (d) { return d.gridPowerKw; },
            hideTopTooltip: true,
        },
        {
            label: "Charger Power",
            accessor: function (d) { return d.chargerDemand; },
            omitFill: true,
            hideTopTooltip: true,
        },
        {
            label: "BESS Charge/Discharge",
            accessor: function (d) { return -d.powernodePowerKw; },
            omitFill: true,
            hideTopTooltip: true,
        },
    ];
    var powerLabelSet = new Set();
    var powerGraphData = powerTrace
        ? powerTrace.map(function (h) {
            return {
                gridLimitKw: h.targetGridLimit,
                gridPowerKw: h.measuredGridPowerKw,
                chargerDemand: h.chargerAcKw,
                powernodePowerKw: h.powernodeKw,
                timestamp: h.timestamp,
            };
        })
        : [];
    var monthlyPeakStats = [
        ["WITH BESS", monthPeak != null ? "".concat(monthPeak.toFixed(0), " kW") : "--"],
        [
            "WITHOUT BESS",
            monthPeakLoadPower != null ? "".concat(monthPeakLoadPower.toFixed(0), " kW") : "--",
        ],
    ];
    return (React.createElement("div", { className: "flex flex-col gap-y-[48px] w-[75%]" },
        React.createElement(DateRangeSelector, { dateRange: dateRange, setDateRange: setDateRange, onHorizonChanged: setTimeHorizon, firstDate: new Date(startDate), hideCustom: true, hideAll: true, hideYtd: true }),
        React.createElement("div", { className: "flex flex-row flex-wrap gap-x-[16px]" },
            React.createElement(MultiStatCard, { title: "MONTHLY PEAK POWER", stats: monthlyPeakStats })),
        React.createElement(LineGraph, { showMouseTooltip: true, labelAccessor: labelAccessor, valueAccessors: powerValueAccessors, data: powerGraphData, yAxisValueToLabel: function (v) { return "".concat(Math.round(v), " kW"); }, manualOrder: true, maxXTicks: powerLabelSet.size, tooltipLabelAccessor: tooltipAccessor }),
        powerNodeSocHistory != null && powerNodeSocHistory.length > 0 && (React.createElement("div", null,
            React.createElement("div", { className: "flex flex-row gap-x-[16px] items-center mb-[16px]" },
                React.createElement("label", { className: "font-N7 text-white" }, "BESS Battery Level"),
                React.createElement(HelpTooltip, { text: "The BESS will recharge to the target SOC. The target may be scheduled to be lower during time of use (TOU) charges to minimize the cost of energy from the grid." })),
            React.createElement(PowerNodeSocGraph, { dataPoints: powerNodeSocHistory !== null && powerNodeSocHistory !== void 0 ? powerNodeSocHistory : [] })))));
};
